import { useLayoutEffect, type RefObject } from "react";

/**
 * Clamp an element's max-height so it ends at the bottom of the viewport when
 * the page is scrolled to the top. Turns a long, page-scrolling grid into a
 * frame that scrolls internally — the horizontal scrollbar sits at the bottom
 * of the visible frame instead of at the bottom of the whole document.
 *
 * Re-measures on window resize and whenever anything above the element
 * changes height (toolbar wrapping, tab bar, banners), via a ResizeObserver
 * on the document root.
 *
 * Pair with the `.tableWrapFrame` class (overflow: auto + sticky thead).
 */
export function useViewportFrame(
  ref: RefObject<HTMLElement | null>,
  opts: { bottomGap?: number; minHeight?: number; active?: boolean; fraction?: number } = {}
): void {
  // `active` lets callers re-arm the measurement after a loading/error early
  // return — the ref is null on the first pass, so the effect must re-run
  // once the framed element actually mounts.
  // `fraction` lets two stacked frames share the viewport: the upper one takes
  // that share of the remaining space and the lower one measures what is left.
  const { bottomGap = 24, minHeight = 240, active = true, fraction = 1 } = opts;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !active) return;
    const apply = () => {
      const docTop = el.getBoundingClientRect().top + window.scrollY;
      const available = (window.innerHeight - docTop - bottomGap) * fraction;
      el.style.maxHeight = Math.max(minHeight, Math.floor(available)) + "px";
    };
    apply();
    window.addEventListener("resize", apply);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(apply) : null;
    ro?.observe(document.documentElement);
    return () => {
      window.removeEventListener("resize", apply);
      ro?.disconnect();
      el.style.maxHeight = "";
    };
  }, [ref, bottomGap, minHeight, active, fraction]);
}
